import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideGrid,
  GuideCard,
  GuideDarkSection
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function Section58DefenceGuide() {
  return (
    <GuideLayout
      slug="section-58-defence"
      category="Legal"
      categoryColor="success"
      title="Section 58 Highways Act Defence: What It Means"
      seoTitle="Section 58 Highways Act Defence: What It Means | Fixtyer"
      description="A practical England-and-Wales guide to the Section 58 defence, the records a highway authority may rely on, and evidence to verify."
      readTime="7 min"
      updatedDate="2026-07-19"
      reviewedDate="2026-07-19"
      datePublished="2026-01-10"
      reviewedFor="England and Wales"
      showCouncilDataCallout
      sources={[
        { name: 'legislation.gov.uk', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/58' },
      ]}
      relatedGuides={[
        { slug: 'section-41-explained', title: 'Section 41 of the Highways Act', readTime: '6 min' },
        { slug: 'prior-reports-explained', title: 'Why Prior Reports Are So Important', readTime: '5 min' },
        { slug: 'understanding-rejection-reasons', title: 'Common Rejection Reasons', readTime: '6 min' },
      ]}
      atAGlance={[
        { label: 'Answer first', value: 'In England and Wales, Section 58 can provide a highway authority with a defence if it establishes that it took the care reasonably required in the circumstances.' },
        { label: 'Records to examine', value: 'The authority’s inspection policy, completed inspection records, defect reports, risk assessments and repair history for the location.' },
        { label: 'Prior-report caution', value: 'A third-party report is a lead to verify against authority records; it does not by itself prove receipt, notice or liability.' },
        { label: 'DfT scope', value: 'DfT ratings cover English highway authorities only and are network-level context, not Section 58 evidence for a specific defect.' },
      ]}
      faqs={[
        {
          question: 'What is Section 58 of the Highways Act?',
          answer: 'Section 58 provides a statutory defence for highway authorities in England and Wales. The authority must establish that it took the care reasonably required to secure that the relevant part of the highway was not dangerous for traffic.',
        },
        {
          question: 'What must the council prove to use this defence?',
          answer: 'The court considers the statutory factors and the evidence for the location. Relevant records may include the inspection and maintenance system, whether it was followed, the character and expected use of the highway, and what the authority knew or could reasonably have been expected to know.',
        },
        {
          question: 'How can I assess a Section 58 defence?',
          answer: 'Compare the authority’s stated policy with its completed inspection, defect and repair records for the location. A prior report may be relevant if the authority confirms it received and matched the report to the same defect, but no single item determines the outcome.',
        },
        {
          question: 'Where can I find evidence of prior reports?',
          answer: 'Check FixMyStreet.com for possible leads at or near your location, then ask the authority for its inspection, report and repair records. A FixMyStreet entry is a lead to verify, not proof the council received or matched it to the same defect.',
        },
        {
          question: 'What if the pothole appeared suddenly?',
          answer: 'A defect that arose between reasonable inspections may support the authority’s defence. The result depends on the statutory factors and location-specific evidence, including the nature of the defect and the inspection system actually used.',
        },
        {
          question: 'Does the council have to prove they inspected the road?',
          answer: 'Inspection records are commonly relevant. Missing or incomplete records may be considered alongside the authority’s policy and the other evidence, but they do not determine the outcome on their own.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        }
        title="Understanding Section 58"
      >
        <p className="mb-4">
          For incidents in England and Wales, a highway authority may rely on Section 58 when responding to a
          pothole damage claim brought under{' '}
          <Link to="/guides/section-41-explained" className={inlineLink}>
            Section 41 (their duty to maintain)
          </Link>
          . It is one possible defence and is relevant to many of the{' '}
          <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>
            most common rejection reasons
          </Link>
          . Understanding the records behind it can help you assess the authority&apos;s response.
        </p>
        <p className="mb-4">
          Keep network condition surveys separate from safety inspections. For example, the{' '}
          <Link to="/councils/dudley" className={inlineLink}>
            Dudley road-maintenance data
          </Link>{' '}
          describes a four-year condition-survey cycle for unclassified roads; that cycle does not
          show when a specific street was safety-inspected.
        </p>
        <GuideTip variant="info" title="The legal position">
          The authority relying on Section 58 must establish the defence. A claimant still needs evidence for
          the incident, damage and alleged breach; independent legal advice may be appropriate.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        }
        title="What Councils Must Prove"
      >
        <p className="text-brand-200 mb-4">
          Records commonly relevant to a Section 58 defence include:
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-accent-500">
            <span className="font-mono text-accent-400 font-bold text-lg">1</span>
            <div>
              <p className="text-white font-semibold mb-1">Adequate Inspection Systems</p>
              <p className="text-brand-300 text-sm">The published system for inspecting and maintaining the relevant class of road.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <span className="font-mono text-brand-300 font-bold text-lg">2</span>
            <div>
              <p className="text-white font-semibold mb-1">Systems Were Followed</p>
              <p className="text-brand-300 text-sm">Completed records showing how the system operated at the location and time.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <span className="font-mono text-brand-300 font-bold text-lg">3</span>
            <div>
              <p className="text-white font-semibold mb-1">Reasonable Response</p>
              <p className="text-brand-300 text-sm">How identified defects were assessed, prioritised and repaired.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <span className="font-mono text-brand-300 font-bold text-lg">4</span>
            <div>
              <p className="text-white font-semibold mb-1">No Prior Knowledge</p>
              <p className="text-brand-300 text-sm">Reports and inspection findings that may show what was recorded about the specific defect.</p>
            </div>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        }
        title="Evidence to compare with the defence"
      >
        <p className="mb-4">
          Compare the response with location-specific records. No category below proves notice, breach or liability
          on its own:
        </p>
        <GuideGrid cols={2}>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
            title="Prior Reports"
            variant="accent"
          >
            A possible third-party report can be a useful lead. Verify whether it concerns the same defect and
            whether the authority received it. Read more about{' '}
            <Link to="/guides/prior-reports-explained" className={inlineLink}>
              previously reported
            </Link>
            {' '}and the limits of third-party records.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
            title="Missing Inspections"
          >
            Compare the inspection policy with completed records. A gap may be relevant but must be assessed with
            the other evidence.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Slow Repair Response"
          >
            Check the recorded risk category, target response and completed repair record rather than assuming a
            missed target establishes liability.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
            title="Obvious Defects"
          >
            Photographs may help show the defect&apos;s condition, but appearance alone does not establish how long it
            existed or what the authority knew.
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        }
        title="Typical Inspection Frequencies"
      >
        <p className="mb-4">
          Councils set inspection frequencies based on road classification — you can see{' '}
          <Link to="/councils" className={inlineLink}>
            how individual councils perform on road maintenance
          </Link>
          . The figures below are general examples, not legal standards; use the authority&apos;s policy and completed
          records for the road:
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="bg-surface-100">
              <tr>
                <th className="text-left p-3 font-semibold text-brand-950">Road Type</th>
                <th className="text-left p-3 font-semibold text-brand-950">Typical Inspection</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              <tr>
                <td className="p-3 text-surface-700">Motorways / Trunk Roads</td>
                <td className="p-3 text-surface-700">Daily to weekly</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Main A & B Roads</td>
                <td className="p-3 text-surface-700">Monthly</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Residential Streets</td>
                <td className="p-3 text-surface-700">Every 3-12 months</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Rural Roads</td>
                <td className="p-3 text-surface-700">Every 6-12 months</td>
              </tr>
            </tbody>
          </table>
        </div>
        <GuideTip variant="warning" title="Note">
          These are typical frequencies — each council sets its own policy. You can request their policy 
          via Freedom of Information.
        </GuideTip>
      </GuideSection>
    </GuideLayout>
  );
}
